// The DOM text supplies layout, accessibility, the color cycle, and the SDR fallback.
// WebGPU's extended rgba16float canvas adds highlights above SDR white.
const shader = `
  @group(0) @binding(0) var mask: texture_2d<f32>;
  @group(0) @binding(1) var maskSampler: sampler;
  @group(0) @binding(2) var<uniform> color: vec4f;
  struct Vertex { @builtin(position) position: vec4f, @location(0) uv: vec2f };
  @vertex fn vertex(@builtin(vertex_index) index: u32) -> Vertex {
    let positions = array<vec2f, 3>(vec2f(-1, -1), vec2f(3, -1), vec2f(-1, 3));
    let p = positions[index];
    return Vertex(vec4f(p, 0, 1), vec2f((p.x + 1) / 2, (1 - p.y) / 2));
  }
  @fragment fn fragment(v: Vertex) -> @location(0) vec4f {
    let alpha = textureSample(mask, maskSampler, v.uv).a;
    return vec4f(color.rgb * 2.2 * alpha, alpha);
  }
`;

/**
 * WebGPU is optional and is not included in this theme's DOM type declarations.
 * @param {HTMLElement} host
 * @param {HTMLElement} text
 * @param {any} gpu
 * @param {AbortSignal} signal
 */
async function enhance(host, text, gpu, signal) {
  const adapter = await gpu.requestAdapter({ powerPreference: 'low-power' });
  if (!adapter || signal.aborted) return;
  const device = await adapter.requestDevice();
  if (signal.aborted) { device.destroy(); return; }

  const canvas = document.createElement('canvas');
  canvas.className = 'ci-cta-hdr';
  canvas.setAttribute('aria-hidden', 'true');
  /** @type {any} WebGPU contexts are newer than the theme's DOM declarations. */
  // @ts-ignore -- The DOM overload does not yet include 'webgpu'.
  const context = canvas.getContext('webgpu');
  if (!context) { device.destroy(); return; }
  let frame = 0;
  /** @type {any} */
  let texture;
  /** @type {any} */
  let binding;
  let visible = false;
  let dirty = true;
  let stopped = false;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const raster = document.createElement('canvas');
  const paint = raster.getContext('2d');
  if (!paint) { device.destroy(); return; }

  const stop = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(frame);
    resize.disconnect();
    intersection.disconnect();
    motion.removeEventListener('change', schedule);
    document.removeEventListener('visibilitychange', schedule);
    document.fonts.removeEventListener('loadingdone', invalidate);
    canvas.remove();
    context.unconfigure();
    texture?.destroy();
    device.destroy();
  };

  // Rasterize actual browser line breaks, including merchant-entered newlines.
  // A mask is rebuilt only for font/layout changes, never for each animation frame.
  const buildMask = () => {
    const bounds = host.getBoundingClientRect();
    const scale = Math.min(devicePixelRatio || 1, 2);
    canvas.width = raster.width = Math.max(1, Math.ceil((bounds.width + 32) * scale));
    canvas.height = raster.height = Math.max(1, Math.ceil((bounds.height + 32) * scale));
    const style = getComputedStyle(host);
    paint.scale(scale, scale);
    paint.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    paint.fillStyle = '#fff';
    paint.shadowColor = '#fff';
    paint.shadowBlur = 4 * scale;
    paint.textBaseline = 'alphabetic';
    const ascent = paint.measureText('Mg').fontBoundingBoxAscent;
    const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let node;
    while ((node = walker.nextNode())) {
      const value = node.textContent || '';
      let run = '';
      /** @type {DOMRect | null} */
      let line = null;
      const flush = () => {
        if (line && run.trim()) paint.fillText(run, line.left - bounds.left + 16, line.top - bounds.top + 16 + ascent);
      };
      for (let i = 0; i < value.length; i++) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const rect = range.getBoundingClientRect();
        if (!rect.width) continue;
        if (line && Math.abs(rect.top - line.top) > 1) { flush(); run = ''; line = null; }
        line ||= rect;
        run += value[i];
      }
      flush();
    }
    texture?.destroy();
    texture = device.createTexture({
      size: [raster.width, raster.height], format: 'rgba8unorm',
      // COPY_DST | TEXTURE_BINDING | RENDER_ATTACHMENT (required for external copies).
      usage: 2 | 4 | 16,
    });
    device.queue.copyExternalImageToTexture({ source: raster }, { texture }, [raster.width, raster.height]);
    binding = device.createBindGroup({ layout: pipeline.getBindGroupLayout(0), entries: [
      { binding: 0, resource: texture.createView() },
      { binding: 1, resource: sampler },
      { binding: 2, resource: { buffer: uniform } },
    ] });
    dirty = false;
  };

  const draw = () => {
    frame = 0;
    if (stopped || signal.aborted || !visible || document.hidden) return;
    try {
      if (dirty) buildMask();
      // CSS remains the single animation clock, including reduced-motion mode.
      const rgb = getComputedStyle(host).color.match(/[\d.]+/g)?.slice(0, 3).map(Number);
      if (!rgb || rgb.length !== 3) return;
      device.queue.writeBuffer(uniform, 0, new Float32Array([...rgb.map(channel => channel / 255), 1]));
      const encoder = device.createCommandEncoder();
      const pass = encoder.beginRenderPass({ colorAttachments: [{
        view: context.getCurrentTexture().createView(),
        clearValue: { r: 0, g: 0, b: 0, a: 0 }, loadOp: 'clear', storeOp: 'store',
      }] });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, binding);
      pass.draw(3);
      pass.end();
      device.queue.submit([encoder.finish()]);
      if (!canvas.isConnected) host.append(canvas);
      if (!motion.matches) frame = requestAnimationFrame(draw);
    } catch (_error) { stop(); }
  };
  function schedule() {
    cancelAnimationFrame(frame);
    if (!stopped) frame = requestAnimationFrame(draw);
  }
  function invalidate() { dirty = true; schedule(); }
  const resize = new ResizeObserver(invalidate);
  const intersection = new IntersectionObserver(entries => {
    visible = entries.some(entry => entry.isIntersecting);
    schedule();
  });
  /** @type {any} */
  let pipeline;
  /** @type {any} */
  let sampler;
  /** @type {any} */
  let uniform;
  try {
    context.configure({ device, format: 'rgba16float', colorSpace: 'srgb',
      alphaMode: 'premultiplied', toneMapping: { mode: 'extended' } });
    if (context.getConfiguration?.().toneMapping?.mode !== 'extended') { stop(); return; }
    const module = device.createShaderModule({ code: shader });
    pipeline = await device.createRenderPipelineAsync({ layout: 'auto',
      vertex: { module, entryPoint: 'vertex' },
      fragment: { module, entryPoint: 'fragment', targets: [{ format: 'rgba16float' }] },
    });
    if (signal.aborted) { stop(); return; }
    sampler = device.createSampler({ magFilter: 'linear', minFilter: 'linear' });
    uniform = device.createBuffer({ size: 16, usage: 64 | 8 }); // UNIFORM | COPY_DST
    signal.addEventListener('abort', stop, { once: true });
    device.lost.then(() => { if (!stopped) stop(); });
    device.addEventListener('uncapturederror', stop, { once: true });
    motion.addEventListener('change', schedule);
    document.addEventListener('visibilitychange', schedule);
    document.fonts.addEventListener('loadingdone', invalidate);
    resize.observe(host);
    intersection.observe(host);
    await document.fonts.ready;
    if (!stopped) invalidate();
  } catch (_error) { stop(); }
}

class CatideaCtaNeon extends HTMLElement {
  /** @type {AbortController | undefined} */
  controller;
  /** @type {MediaQueryList | undefined} */
  hdr;
  connectedCallback() {
    this.hdr = matchMedia('(dynamic-range: high)');
    this.hdr.addEventListener('change', this.refresh);
    this.refresh();
  }
  disconnectedCallback() {
    this.hdr?.removeEventListener('change', this.refresh);
    this.controller?.abort();
  }
  refresh = () => {
    this.controller?.abort();
    // @ts-ignore -- Feature-detect the optional WebGPU API at runtime.
    const gpu = navigator.gpu;
    const text = this.querySelector('[data-cta-text]');
    if (!this.hdr?.matches || !gpu || !(text instanceof HTMLElement)) return;
    this.controller = new AbortController();
    enhance(this, text, gpu, this.controller.signal).catch(() => {
      // The original DOM text and CSS glow remain visible if HDR is unavailable.
    });
  };
}

if (!customElements.get('catidea-cta-neon')) customElements.define('catidea-cta-neon', CatideaCtaNeon);
